import { useId } from 'react';
import { IconBuilding, IconUser } from '@iwoca/icons';
import { IconMoneyPound1 } from '@iwoca/icons/brand';
import clsx from 'clsx';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { useGetCommissionOverview } from 'lendingApi/lendingApiHooks';
import Skeleton from 'react-loading-skeleton';
import {
  getCommissionDateRange,
  getCommissionMonthLabel,
} from './CommissionOverview.helpers';
import styles from './CommissionOverview.module.css';
import { CommissionTooltip } from './CommissionTooltip/CommissionTooltip';

type TProps = {
  introducerId: string;
  isMasterBroker: boolean;
  variant?: 'desktop' | 'mobile';
};

const ERROR_VALUE = '£–';

export const CommissionOverview = ({
  introducerId,
  isMasterBroker,
  variant = 'desktop',
}: TProps) => {
  const { data, isLoading, isError } = useGetCommissionOverview(introducerId);
  const { formatCurrency } = useNumberFormat();
  const individualTooltipId = useId();
  const brokerageTooltipId = useId();

  const monthLabel = getCommissionMonthLabel();
  const dateRange = getCommissionDateRange();

  const hasError = isError || (!isLoading && !data);

  const introducerBreakdown = data?.data.introducer;
  const accountBreakdown = data?.data.account;

  const showBrokerageTotal =
    isLoading || hasError
      ? isMasterBroker
      : isMasterBroker && !!accountBreakdown;

  const renderFigureValue = (formattedValue: string) => {
    if (isLoading) {
      return (
        <Skeleton
          borderRadius={10}
          width={52}
          height={14}
          baseColor="var(--color-secondary-90)"
        />
      );
    }
    return hasError ? ERROR_VALUE : formattedValue;
  };

  const individualBrokerFormatted = introducerBreakdown
    ? formatCurrency(introducerBreakdown.this_month_commission_total)
    : ERROR_VALUE;
  const brokerageFormatted = accountBreakdown
    ? formatCurrency(accountBreakdown.this_month_commission_total)
    : ERROR_VALUE;

  return (
    <>
      <span
        className={clsx(styles.pill, { [styles.mobile]: variant === 'mobile' })}
        data-testid="commission-overview"
        data-tooltip-id={showBrokerageTotal ? undefined : individualTooltipId}
      >
        <IconMoneyPound1 className={styles.leadingIcon} aria-hidden />
        <span className={styles.label}>{monthLabel}</span>

        {showBrokerageTotal ? (
          <span className={styles.figures}>
            <span
              className={styles.figure}
              data-testid="commission-individual"
              data-tooltip-id={individualTooltipId}
            >
              <IconUser className={styles.figureIcon} aria-hidden />
              {renderFigureValue(individualBrokerFormatted)}
            </span>
            <span className={styles.divider} aria-hidden />
            <span
              className={styles.figure}
              data-testid="commission-brokerage"
              data-tooltip-id={brokerageTooltipId}
            >
              <IconBuilding className={styles.figureIcon} aria-hidden />
              {renderFigureValue(brokerageFormatted)}
            </span>
          </span>
        ) : (
          <span className={styles.figure} data-testid="commission-individual">
            {renderFigureValue(individualBrokerFormatted)}
          </span>
        )}
      </span>

      <CommissionTooltip
        tooltipId={individualTooltipId}
        paidTo="for your applications"
        dateRange={dateRange}
        breakdown={introducerBreakdown}
        hasError={hasError}
      />
      {showBrokerageTotal && (
        <CommissionTooltip
          tooltipId={brokerageTooltipId}
          paidTo="to your brokerage"
          dateRange={dateRange}
          breakdown={accountBreakdown}
          hasError={hasError}
        />
      )}
    </>
  );
};
